import React, { useEffect, useState, useMemo, useRef } from 'react';
import { toPng } from 'html-to-image';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { CHAT_ICON, MENU_ICON, ZOOM_ICON } from '../../utills/image-constant';
import { divisionLevelHeatMap } from '../../services/api-services';
import Remark from '../../admin/components/Charts/Remark';
import ZoomModal from './zoomModal';
import Modal from '../../admin/components/common/Model';
import generateChartImage from '../../admin/components/Charts/generateChartImage';

const DivisionLevelHeatmap = ({
  selectedCompanyId,
  selectedCampaignId,
  type,
  graphName,
  title = 'Demographic Dashboard – Division Level',
}) => {
  const [divisionHeatmapData, setDivisionHeatmapData] = useState([]);
  const [divisionData, setDivisionData] = useState([]);
  const [remark, setRemark] = useState('');
  const [remarkId, setRemarkId] = useState(null);
  const [isAttempt, setIsAttempt] = useState();
  const [loading, setLoading] = useState(false);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkText, setRemarkText] = useState('');
  const modalRef = useRef(null);
  const chartRef = useRef(null);
  const [colorRanges, setColorRanges] = useState([]);
  const modalId = 'divisionLevelHeatmapZoom';

  //  Fetch Data
  useEffect(() => {
    const getHeatmapDivisionData = async () => {
      try {
        const response = await divisionLevelHeatMap(
          selectedCompanyId,
          selectedCampaignId,
          'department',
          type,
          graphName
        );
        if (response?.data?.responseCode === 200) {
          const data = response?.data;
          setRemarkText(data?.remark || '');
          setIsAttempt(data?.isAttempt ?? 0);
          setRemarkId(data?.remarkId || null);
          setDivisionHeatmapData(data?.responseBody || []);
          setDivisionData(data?.responseBody || []);
          setColorRanges(data?.colorList || []);
          setTimeout(
            () => generateHtmlImage('demographic_dashboard_division_level'),
            500
          );
        }
      } catch (err) {
        console.error(err);
      }
    };
    getHeatmapDivisionData();
  }, [selectedCompanyId, selectedCampaignId, type, graphName]);

  //  Generate chart image
  const generateHtmlImage = async (elementId) => {
    const node = document.getElementById(elementId);
    if (!node) return;
    try {
      const dataUrl = await toPng(node);
      const chartData = JSON.parse(localStorage.getItem('chartImageData')) || [];
      const updatedChartData = chartData.map((item) =>
        item.id === 'demographic_dashboard_division_level'
          ? { ...item, image: dataUrl, remark }
          : item
      );
      localStorage.setItem('chartImageData', JSON.stringify(updatedChartData));
    } catch (error) {
      console.error('Error generating image from HTML:', error);
    }
  };

  //  Get color by percentage
  const getColorForPercentage = useMemo(
    () => (percentage) => {
      const range = colorRanges.find(
        (range) =>
          percentage >= range.rangeFrom && percentage <= range.rangeTo
      );
      return range ? range.colorName : '#000000';
    },
    [colorRanges]
  );

  //  Processed heatmap data
  const processedHeatmapData = useMemo(() => {
    return (
      divisionHeatmapData
        ?.map((division) => {
          const validDimensions =
            division.dimensionOverallHeatmapChartResponseList?.filter(
              (dim) => dim.percentage >= 0
            ) || [];
          return { ...division, validDimensions };
        })
        .filter((division) => division.validDimensions.length > 0) || []
    );
  }, [divisionHeatmapData]);

  //  Export to Excel (Detailed)
  const exportToExcel = (data, fileName = 'Demographic Dashboard – (Division Level)') => {
    if (!data || !Array.isArray(data)) return;

    const title = ['Demographic Dashboard – (Division Level)'];
    const headers = [
      'Department Name',
      'Dimension Name',
      'Dimension Percentage',
      'Subdimension Name',
      'Subdimension Percentage',
    ];

    const rows = [];
    data.forEach((dept) => {
      dept.dimensionOverallHeatmapChartResponseList?.forEach((dimension) => {
        dimension.subDimensionOverallHeatmapChartResponseList?.forEach((sub) => {
          rows.push([
            dept.name,
            dimension.dimensionName,
            dimension.percentage,
            sub.subDimensionName,
            sub.percentage,
          ]);
        });
      });
    });

    const worksheet = XLSX.utils.aoa_to_sheet([title, [], headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Department Heatmap');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    saveAs(blob, fileName);
  };

  //  Export Summary Excel
  const exportAsExcel = () => {
    if (!divisionData || !Array.isArray(divisionData)) return;
    const headers = ['Department Name', 'Dimension Name', 'Percentage'];
    const rows = [];
    const percentageValues = [];

    divisionData.forEach((role) => {
      role.dimensionOverallHeatmapChartResponseList?.forEach((dimension) => {
        rows.push([role.name, dimension.dimensionName, dimension.percentage]);
        percentageValues.push(dimension.percentage);
      });
    });

    const total = percentageValues.reduce((acc, val) => acc + val, 0);
    const average = percentageValues.length ? total / percentageValues.length : 0;
    rows.push(['', 'Grand Total (Average)', average.toFixed(2)]);

    const worksheet = XLSX.utils.aoa_to_sheet([[title], [], headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Division Summary');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      `${title}.xlsx`
    );
  };

  //  Prepare Chart Data
  const categories = [];
  let data = [];

  if (divisionData?.length > 0) {
    divisionData.forEach((item) => {
      item.dimensionOverallHeatmapChartResponseList?.forEach((dimension) => {
        if (!categories.includes(dimension.dimensionName)) {
          categories.push(dimension.dimensionName);
        }

        const entry = data.find((d) => d.name === item.name);
        if (entry) {
          entry.values.push(parseFloat(dimension.percentage.toFixed(2)));
        } else {
          data.push({
            name: item.name,
            values: [parseFloat(dimension.percentage.toFixed(2))],
          });
        }
      });
    });
  }

  //  Grand Total Row
  const grandTotal = categories.map((_, idx) => {
    const sum = data.reduce((total, row) => total + (row.values[idx] || 0), 0);
    const average = sum / data.length;
    return parseFloat(average.toFixed(2));
  });

  //  Get color dynamically based on API color ranges
  const getColorForValue = (value) => {
    for (let i = 0; i < colorRanges.length; i++) {
      const range = colorRanges[i];
      if (value >= range.rangeFrom && value <= range.rangeTo) {
        return range.colorName;
      }
    }
    return '#D3D3D3';
  };

  const grandTotalColor =
    grandTotal.length > 0 ? getColorForValue(grandTotal[0]) : '#000000';

  //  Build chart series AFTER data is ready
  const series = [
    ...data.map((row) => ({
      name: row.name,
      data: row.values,
    })),
    {
      name: 'Grand Total (Average)',
      data: grandTotal,
      color: grandTotalColor,
    },
  ];

  //  Dynamic color scale
  const colorScaleRanges = colorRanges.map((range) => ({
    from: range.rangeFrom,
    to: range.rangeTo,
    color: range.colorName,
    name: `${range.rangeFrom}% - ${range.rangeTo}%`,
  }));

  //  Chart options
  const options = {
    chart: {
      id: 'demographic_dashboard_division',
      type: 'heatmap',
      toolbar: { show: false },
      events: {
        mounted: () => {
          setTimeout(() => {
            generateChartImage(
              'demographic_dashboard_division',
              'demographic_dashboard_division',
              remark
            );
          }, 100);
        },
      },
    },
    plotOptions: {
      heatmap: { colorScale: { ranges: colorScaleRanges } },
    },
    xaxis: {
      categories,
      labels: { style: { fontSize: '12px', fontWeight: 'bold' } },
      position: 'top',
    },
    yaxis: {
      labels: {
        style: { fontSize: '12px', fontWeight: 'bold' },
        formatter: (value) =>
          value === 'Grand Total (Average)' ? 'Grand Total' : value,
      },
      reversed: true,
    },
    dataLabels: {
      enabled: true,
      formatter: (value) => `${value}%`,
      style: { fontSize: '10px', fontWeight: 'bold', colors: ['#000'] },
    },
    tooltip: { enabled: true, y: { formatter: (value) => `${value}%` } },
    legend: { show: false },
  };

  // 📤 Export as Image
  const exportAsImage = async () => {
    const chartElement = chartRef.current?.container;
    if (!chartElement) return;
    const canvas = await html2canvas(chartElement);
    const imgData = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = imgData;
    link.download = `${title}.png`;
    link.click();
  };

  // 📤 Export as PDF
  const exportAsPDF = async () => {
    const chartElement = chartRef.current?.container;
    if (!chartElement) return;
    const canvas = await html2canvas(chartElement);
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'landscape' });
    const imgWidth = 280;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
    pdf.save(`${title}.pdf`);
  };

  return (
    <>
      <div className="card" id="demographic_dashboard_division_level">
        {processedHeatmapData.length > 0 ? (
          processedHeatmapData.map((division, divisionIndex) => (
            <div
              key={divisionIndex}
              className={`division-section pdf-chart chart11-${divisionIndex}`}
              data-heading="Demographic Dashboard – (Division Level)"
              data-remark={remark}
            >
              <div className="d-flex justify-content-between align-items-center overall-text mb-2 p-2">
                <h5 className="m-0">{title}</h5>
                <div className="d-flex align-items-center">
                  <button
                    className="bg-transparent border-0 me-2"
                    data-bs-toggle="modal"
                    data-bs-target={`#${modalId}`}
                  >
                    <img src={ZOOM_ICON} alt="zoom" />
                  </button>
                  <div className="dropdown">
                    <button
                      className="icon-btn bg-transparent"
                      type="button"
                      data-bs-toggle="dropdown"
                    >
                      <img src={MENU_ICON} alt="menu" />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={exportAsExcel}
                        >
                          Download Excel
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item" onClick={exportAsPDF}>
                          Export PDF
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={exportAsImage}
                        >
                          Download Image
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="legend-container">
                <div className="legend-row">
                  {colorRanges.map((item, index) => (
                    <div key={index} className="legend-item">
                      <div
                        className="legend-color"
                        style={{ backgroundColor: item.colorName }}
                      ></div>
                      <span className="legend-label">
                        {item.rangeFrom}% - {item.rangeTo}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Header */}
              <div className="header-row">
                {division.validDimensions.map((dimension) => (
                  <div
                    key={dimension.id}
                    className="header-cell"
                    style={{
                      backgroundColor: getColorForPercentage(
                        dimension.percentage?.toFixed(2)
                      ),
                    }}
                  >
                    <p>{dimension.dimensionName}</p>
                    <p>{dimension.percentage.toFixed(2)}%</p>
                  </div>
                ))}
              </div>

              {/* Data Rows */}
              <div className="data-row">
                {division.validDimensions.map((dimension) => (
                  <div key={dimension.id} className="column">
                    {dimension.subDimensionOverallHeatmapChartResponseList?.map(
                      (subDimension) => (
                        <div
                          key={subDimension.id}
                          className="data-cell"
                          style={{
                            backgroundColor: getColorForPercentage(
                              subDimension.percentage?.toFixed(2)
                            ),
                          }}
                        >
                          <p>{subDimension.subDimensionName}</p>
                          <p>{subDimension.percentage?.toFixed(2)}%</p>
                        </div>
                      )
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <p style={{ textAlign: 'center', marginTop: '20px' }}>
            No data available to display.
          </p>
        )}

        {/* Chat / Remark Button */}
        <div className="d-flex justify-content-end my-2">
          <button
            title="Add Comment"
            onClick={() => {
              setShowRemarkModal(true);
              setTimeout(() => {
                if (modalRef.current) modalRef.current.click();
              }, 0);
            }}
            className="border-0 bg-transparent"
          >
            <img src={CHAT_ICON} alt="chat" />
          </button>
        </div>

        {/* Remark Modal */}
        {showRemarkModal && (
          <Modal modalClick={modalRef} title="Remark" buttonLable="">
            <Remark
             selectedCompanyId={selectedCompanyId}
            selectedCampaignId={selectedCampaignId}
            type={type}
            graphName={graphName}
            initialRemark={remarkText}
            remarkId={remarkId}
            isAttempt={0}
            setRemarkprops={setRemarkText}
            setRemarkIdProps={setRemarkId}
            onCloseModal={() => setShowRemarkModal(false)}
            />
          </Modal>
        )}

        {/* Zoom Modal */}
        <ZoomModal
          id={modalId}
          title={title}
          chartOptions={options}
          seriesData={series}
          type="heatmap"
          height={400}
             remarkText={remarkText}
        setRemarkText={setRemarkText}
        showRemarkModal={showRemarkModal}
        setShowRemarkModal={setShowRemarkModal}
        modalRef={modalRef}
        exportSummaryToExcel={exportAsExcel}
        exportAsPDF={exportAsPDF}
        exportAsImage={exportAsImage}
        />
      </div>
    </>
  );
};

export default DivisionLevelHeatmap;
